
import { useState, useEffect } from 'react';

/**
 * Hook to detect if the current viewport is mobile size
 * @param breakpoint - CSS breakpoint in pixels for mobile (default: 768)
 * @returns Boolean indicating if screen is mobile size
 */
export const useIsMobile = (breakpoint: number = 768): boolean => {
  // Initialize with a check so there's no flash of incorrect layout
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < breakpoint;
    }
    return false;
  });
  
  useEffect(() => {
    // Check on initial render
    setIsMobile(window.innerWidth < breakpoint);
    
    // Add window resize handler
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [breakpoint]);
  
  return isMobile;
};

/**
 * Hook to detect specific device type based on viewport width
 * @returns Object with boolean flags for different device types
 */
export const useDeviceDetect = () => {
  const [device, setDevice] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false
  });
  
  useEffect(() => {
    // Check on initial render
    handleResize();
    
    // Add window resize handler
    function handleResize() {
      const width = window.innerWidth;
      setDevice({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024
      });
    }
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return device;
};
